// AI_CONTEXT: {
//   "docs": [
//     "epics/202609212003_CEF_confirm-email-frontend/tickets/CEF-027-mobile-sheet-above-keyboard"
//   ],
//   "purpose": "Main app orchestrator: page lifecycle, routing, overlay stack, tooltips, global events",
//   "version": "1.3.0",
//   "priority": "core",
//   "stability": "volatile"
// }
/**
 * Base - Main application orchestrator (extended by project-specific App.js)
 *
 * SINGLETON: The central controller for the entire application. Base is extended by
 * project-specific App.js which adds routing configuration and custom behavior.
 * RequireJS loads App.js on document ready, kicking off the entire client-side application.
 *
 * CRITICAL: This is the heart of the framework. Base manages:
 * - Page lifecycle (loading, showing, destroying pages)
 * - Overlay stack (opening, closing, stacking modals)
 * - Routing (handlePath() method for URL → Page mapping)
 * - Global state (configuration, user data, query params)
 * - URL management (hash, query strings)
 * - UI blocking (during AJAX requests)
 * - Notifications (toast messages)
 * - Tooltips (global tooltip management)
 * - Cookie management
 * - localStorage integration
 *
 * USAGE PATTERN:
 * Project-specific App.js extends Base and adds:
 * ```javascript
 * inherit({
 *     parent: 'Base',
 *     singleton: true,
 *     prepare(setup) {
 *         this.registerPaths();  // Define route handlers
 *     },
 *     api: {
 *         registerPaths: {
 *             value() {
 *                 this.handlePath({
 *                     path: '/my-page',
 *                     handler: (request, response) => {
 *                         response.page = 'MyPage';
 *                     }
 *                 });
 *             }
 *         }
 *     }
 * });
 * ```
 *
 * ROUTING:
 * - handlePath() registers URL pattern → Page mapping
 * - Supports wildcards: '/groups/*'
 * - Provides request object (path, slugs, queryParams)
 * - Handler sets response.page and optional response.setup
 *
 * PAGE LIFECYCLE:
 * 1. URL change detected
 * 2. Route handler determines page
 * 3. Previous page destroyed (if exists)
 * 4. New page loaded and constructed
 * 5. Page shown, URL updated
 *
 * OVERLAY MANAGEMENT:
 * - Overlays stack on top of each other
 * - Backdrop management
 * - Escape key handling
 * - Automatic cleanup when closed
 *
 * @class Base
 * @extends Controller
 * @singleton
 *
 * @see App.js in project repositories for extension pattern
 */
define("Base", [`Controller`, `module`, `jquery`, `requirejs-config`], function (Super, Self, jQuery) {
	const DEFAULT_MODE = `web`;
	const DEFAULT_PLATFORM = `web`;
	const SCROLL_DURATION = 250;
	const EXTERNAL_LOGIN_TIMEOUT = 180; // default to three minutes
	const { location } = window;
	const { search: rawQueryString, hash: rawHashString } = location;
	const isFileSystem = location.protocol.indexOf(`http`) !== 0; // detects when this is a local filesystem, for unit testing
	const configPromises = {};
	const urlHandlers = [];

	window.onpageshow = (evt) => {
		const { persisted } = evt;

		if (persisted) {
			const { location } = window;
			const { href } = location;

			location.href = href;
		}
	};

	return Super.inherit({
		namespace: Self.id,
		requires: [`XHR`, `Geolocation`, `Insights`, `Locale`, `Protection`, `Social`, `Storage`, `Time`, `jquery`],
		defaults: {
			block: false, // avoid a circular dependency, Base is required for 'blocking'
			container: document.getElementById(`site`),
		},
		prepare(setup) {
			const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
			const { Storage, Time, data, instances } = this;
			const timestamp = Time.getTime();
			const localStorageKey = `tixr-user-history`;
			const savedQueryParamsKey = `queryParams`;
			const localStorageDuration = 31536000000; // 365 days * 24 hours * 60 minutes * 60 seconds * 1000 ms
			const localStorageExpiration = timestamp + localStorageDuration;

			// important to update before calculating localStorage stuff
			this.assign(data, { localStorageExpiration, localStorageKey, savedQueryParamsKey });

			const firstPartyStorage = this.getFirstPartyStorage();
			const savedQueryParams = this.getSavedQueryParams();
			const { userId: savedUserId, userEmailHash: savedUserEmailHash } = firstPartyStorage;

			this.externalCallbacks = {}; // used to keep track of available native app callbacks

			instances.page = null;
			instances.overlays = [];

			this.assign(data, {
				blockers: [],
				config: {}, // used to share global configuration
				initialized: false, // keep track of whether the first page has loaded
				mouseTrackingEnabled: false,
				notifications: [],
				pageChangeHandlers: [],
				pageConfig: {},
				savedUserId,
				savedUserEmailHash,
				scrollHandlers: [], // used to keep track of multiple instances trying to disable
				stateChanging: false,
				tickHandlers: [],
				tickIntervalId: null,
				tooltips: [],
				tooltipTransitionIn: `pop`,
				tooltipTransitionOut: `height`,
			});

			this.setQueryString(rawQueryString);
			this.setHashString(rawHashString);

			this.each(savedQueryParams, (value, key) => {
				if (value.expiration < timestamp) {
					delete savedQueryParams[key];
				}
			});

			this.each(data.queryParams, (value, key) => {
				if (this.isString(value)) {
					const expiration = timestamp + THIRTY_DAYS_MS;
					savedQueryParams[key] = { expiration, value };
				}
			});

			data.savedQueryParams = savedQueryParams;

			const serializedQueryParams = JSON.stringify(savedQueryParams);
			Storage.set({ key: savedQueryParamsKey, value: serializedQueryParams });

			// TODO: remove after this has been live 30 days, when all cookies have expired
			this.deleteCookie(savedQueryParamsKey);
			this.deleteCookie(localStorageKey);
		},
		constructor(setup) {
			const siteElement = this.get(`#site`, document.body);
			const { Config, DOM, XHR, data, elements } = this; // shorthand references

			this.defaults(setup, {
				container: siteElement,
			});

			if (!this.isElement(setup.container)) {
				this.log({
					type: `warning`,
					message: `Base(): setup.container not defined, unit test logic needs revisiting`,
					priority: 50,
				});
			}

			this.setMode();

			this.each([`site`, `tooltips`, `notifications`, `page`, `overlay-background`, `overlay`, `loading`], (id) => {
				// do the basic site setup, if the page isn't already rendered
				let el = document.getElementById(id);

				if (!this.isElement(el)) {
					// the element isn't already on the page
					el = document.createElement(`div`); // create a new one
					el.id = id; // set it's id attribute

					if (id === `site`) {
						// append the newly created element to the document body
						document.body.appendChild(el);
					} else {
						// append the layer element to the site element
						elements.site.appendChild(el);

						if (this.includes([`overlay`, `loading`], id)) {
							const bg = document.createElement(`div`);
							bg.className = `background`;
							el.appendChild(bg);
						}
					}
				}

				elements[id] = el; // save it into the elements reference object
			});

			elements.notifyContainer = document.createElement(`ul`);
			elements.notifyContainer.id = `notify`;
			elements.notifications.appendChild(elements.notifyContainer);

			const existingElement = DOM.get(`> .Module`, elements.overlay);
			if (this.isElement(existingElement)) {
				DOM.remove(existingElement);
			}

			// the handler for closing user dropdown notifications
			DOM.on({
				target: elements.notifyContainer,
				event: `click`,
				filter: `.icon-close`,
				handler: (evt) => {
					const target = DOM.closest(evt.target, `li`);
					const dimensions = DOM.getDimensions(target);
					let promise;

					if (dimensions.isVisible) {
						promise = DOM.hide({
							target,
							transition: `height`,
						});
					} else {
						promise = this.when({
							promise: target,
						});
					}

					this.when({
						promise,
						then: () => {
							const { notifications } = data;
							// Remove the notification from the array
							const query = { element: target };
							const notification = this.extractWhere(notifications, query);
							const { deferred } = notification;

							DOM.remove(target); // Remove the element from the DOM
							deferred.resolve(); // Resolve the Promise that was returned from the notify call
						},
					});
				},
			});

			let repaintPending;
			let lastScrollTop;
			function onScroll(evt) {
				const { scrollY: siteScrollTop } = window;
				data.scrollTop = siteScrollTop;

				if (repaintPending === true || data.scrollTop === lastScrollTop) {
					// page hasn't repainted or scroll amount hasn't changed
					return;
				}

				if (this.isUndefined(lastScrollTop)) {
					lastScrollTop = 0;
				}

				window.requestAnimationFrame(() => {
					const { scrollHandlers, scrollTop } = data;
					const scrollDelta = lastScrollTop - scrollTop;
					const details = {};

					Object.defineProperties(details, {
						// make read-only
						scrollDelta: { value: scrollDelta },
						scrollTop: { value: scrollTop },
					});

					for (var i = 0; i < scrollHandlers.length; i++) {
						const handler = scrollHandlers[i];
						const { fn, owner } = handler;
						fn.call(owner, details);
					}

					lastScrollTop = scrollTop;
					repaintPending = false;
				});

				repaintPending = true;
			}

			DOM.on({
				event: `scroll`,
				passive: true,
				handler: onScroll,
				target: window,
			});

			XHR.addErrorHandler({
				priority: `high`, // first attempt at solving XHR problems
				handler: async (reason) => {
					try {
						const error = JSON.parse(reason.responseText);
						const { docType, code } = error;

						if (docType === `STANDARD_ERROR` && code === 1051) {
							// CAPTCHA_REQUIRED, but not sent
							// load the dependency
							await this.require({ deps: [`Captcha`] });

							// obtain a response
							const recaptchaResponse = await this.Captcha.getResponseToken();

							// attach the response to the retry request
							return { recaptchaResponse };
						}
					} catch (err) {
						throw reason; // throw original reason if there was a parsing error
					}

					throw reason; // Re-throw the error for consumers to handle
				},
			});

			XHR.addErrorHandler({
				priority: `low`, //
				handler: (reason) => {
					// generic error handling for XHR errors not caught by the XHR handler or consuming module
					var error;

					switch (reason.status) {
						case 400: // general request error, check for a message to relay to the user
						case 401: // authenticated user trying to access unauthorized content
							error = JSON.parse(reason.responseText);

							if (error.docType === `STANDARD_ERROR`) {
								if (error.code === 1053) {
									// COPA_COMPLIANCE_AGE_RESTRICTION_ERROR
									this.loadPath({ href: `/` });

									this.require({
										deps: [`User`],
										then: () => this.User.logOut(),
									});
								}
							}

							if (error.docType === `VALIDATION_ERROR`) {
								this.each(error.errors, (validation) => {
									this.notifyUser({
										type: `error`,
										message: validation.errorMessage,
									});
								});
							} else if (this.isString(error.message, 1)) {
								// there's a message property on the parsed object
								this.notifyUser({
									type: `error`,
									message: error.message,
								});
							}
							break;

						case 503: // Severe server error
						case 504:
							this.notifyUser({
								type: `error`,
								message: `Unable to reach the server`,
							});
							break;

						default:
							if (this.isInt(reason.status, 400)) {
								// HTTP error
								if (this.isString(reason.responseText, 1)) {
									try {
										error = JSON.parse(reason.responseText);

										if (this.isString(error.message, 1)) {
											error = error.message;
										} else {
											error = `An unexpected error occurred`;
										}
									} catch (err) {
										error = `An unexpected error occurred`;
									}
								} else {
									error = `An unexpected error occurred`;
								}

								this.notifyUser({
									type: `error`,
									message: error,
								});
							}
					}

					throw reason; // Re-throw the error for consumers to handle
				},
			});

			if (Config.ENVIRONMENT === `dev`) {
				this.require({
					deps: [`livereload`],
					fail: (reason) => {
						// swallow error
					},
				});
			}

			this.defer({
				// don't return; the page would wait forever for App to be ready, while App is waiting on the Page
				then: async () => {
					if (!this.alive) {
						// NOTE: this is for unit testing purposes, wouldn't normally call this.destroy() on the main app
						this.log({
							type: `warning`,
							message: `Deferred Base init canceled.`,
						});
						return;
					}

					const jspContainer = DOM.get(`.Page`, elements.page);
					const jspName = this.isString(window.pageName) ? window.pageName : null;
					const jspSetup = this.isObject(window.pageSetup) ? window.pageSetup : {};
					const { loadedCss, location } = window;
					const { hash: hashParams, pathname: path, search: queryParams } = location;
					let details;

					if (this.isDefined(window.pageName)) {
						delete window.pageName;
					}

					if (this.isDefined(window.pageSetup)) {
						delete window.pageSetup;
					}

					this.defaults(jspSetup, {
						container: jspContainer,
						title: document.title,
					});

					this.each(loadedCss, (href) => DOM.registerHref({ href }));

					await this.loadPageConfig({ path, queryParams });

					if (this.isString(jspName)) {
						await this.loadPage({
							name: jspName,
							setup: jspSetup,
						});

						// page construction is complete
						details = {
							name: jspName,
							setup: jspSetup,
							href: path,
							title: data.title,
						};
					} else {
						details = await this.loadUrl({
							href: path,
							setup: jspSetup,
						});
					}

					// page was successfully loaded
					this.removeClass(`loading`, document.body);

					const href = `${details.href}${queryParams}${hashParams}`;

					this.replaceState({
						href,
						name: details.name,
						setup: details.setup,
						title: document.title,
					});

					window.console.log(`Updated href: ${href}`);

					this.onTick({
						fn: this.positionTooltips,
					});

					this.tabulate();

					data.initialized = true;
				},
			});
		},

		listeners: [
			{
				event: `popstate`,
				target: window,
				matchContainer: false,
				async handler(evt) {
					const { data } = this;

					// page has changed (browser navigation)
					if (data.stateChanging) {
						// page is being constructed, ignore the change
						const message = `history: popstate triggered, skipping`;
						this.log({ message });
						return;
					}

					const { originalEvent } = evt;
					const { state } = originalEvent;
					const { name, href, setup } = state;

					await this.loadPageConfig({ path: href });
					return this.loadPage({ name, setup });
				},
			},
			{
				event: `click`,
				filter: `a`,
				target: document,
				matchContainer: false,
				handler(evt) {
					const { location } = document;
					const { host, protocol } = location;
					const { currentTarget: link } = evt;
					const currentDomain = `${protocol}//${host}`;
					const rawHref = link.getAttribute(`href`); // mainly used for detecting '#'
					const { href, target } = link;
					const targetingDefault = target === ``;
					const targetingSelf = targetingDefault || target === `_self`;

					if (!this.isString(href, 1) || rawHref === `#`) {
						// not a real link
						evt.preventDefault(); // prevent attempting to navigate
					} else if (href[0] !== `#` && href.indexOf(currentDomain) !== 0) {
						// an external link, don't prevent default
						if (targetingDefault) {
							link.target = `_blank`; // open in a new window
							link.rel = `noopener noreferrer`;
						}
					} else if (href[0] !== `#` && targetingSelf) {
						// internal link, see if we can route it
						evt.preventDefault();
						this.loadPath({
							href: href.replace(currentDomain, ``),
						});
					}
				},
			},
			{
				event: `resize`,
				target: window,
				matchContainer: false,
				trigger: true,
				handler(evt) {
					// on window resize
					const container = this.container;

					this.positionTooltips();
					this.tabulate();

					// TODO: unit testing should be adjusted, so a container is always provided
					if (!this.isElement(container)) {
						return;
					}
				},
			},
		],

		api: {
			/**
			 * @method Base#tabulate
			 */
			tabulate: {
				// keeping any extra viewport / page / overlay related behavior up-to-date
				debounce: 250, // limit how often to fire
				value: typeof window.calculateViewport === `function` ? window.calculateViewport : function () {},
			},

			/**
			 * Create a serialized representation of an array or plain object, for use in a URL query string or ajax request.
			 * @method Base.serializeParams
			 * @public
			 */
			serializeParams: {
				static: true,
				public: true,
				value: jQuery.param,
			},

			/**
			 * Used for inheriting App to register URLs using this.handleUrl()
			 * @method Base#handlePath
			 */
			handlePath: {
				value(options) {
					const { handler } = options;
					let { paths } = options;

					if (!this.isArray(paths, 1)) {
						paths = [options.path];
					}

					urlHandlers.push({
						handler,
						paths: this.map(paths, (absolute) => {
							const relative = absolute.replace(/^\//, ``);
							const allPieces = relative.split(`/`);
							const pieces = this.filter(allPieces, ({ length }) => length > 0);

							return { absolute, pieces, relative };
						}),
					});
				},
			},

			/**
			 * @method Base#loadUrl
			 */
			loadUrl: {
				async: true,
				value(options) {
					const { data } = this;
					const { pageConfig } = data;
					const { href } = options;
					const leadingRegex = /^[a-z]+:\/{2}[^/]+/; // any protocol + domain, including leading slash
					const multiSlashRegex = /\/+/; // any number of duplicate slashes
					const leadingSlashRegex = /^\//; // leading slash
					const trailingSlashRegex = /\/$/; // trailing slash
					const path = href.replace(leadingRegex, ``).replace(multiSlashRegex, `/`).replace(trailingSlashRegex, ``);
					const relativePath = path.replace(leadingSlashRegex, ``);
					const allSlugs = relativePath.split(`/`);
					const slugs = this.filter(allSlugs, (slug) => this.isString(slug, 1));
					const request = { path, slugs };
					const response = {};
					let matchingUrlHandler;

					matchingUrlHandler = this.find(urlHandlers, (urlHandler) => {
						const { paths } = urlHandler;
						let isMatch = false;

						this.find(paths, (path) => {
							const { pieces } = path;

							if (pieces.length !== slugs.length) {
								return false;
							}

							for (var i = 0; i < pieces.length; i++) {
								const piece = pieces[i];

								if (piece !== `*` && piece !== slugs[i]) {
									return false;
								}
							}

							isMatch = true;
							return true;
						});

						return isMatch;
					});

					if (this.isObject(matchingUrlHandler)) {
						matchingUrlHandler.handler.call(this, request, response);

						if (this.isString(response.page)) {
							const { page: name } = response;
							const setup = {};

							if (this.isObject(pageConfig.setup)) {
								this.assign(setup, pageConfig.setup); // copy over any setup properties from the server page config
							}

							if (this.isObject(options.setup)) {
								this.assign(setup, options.setup);
							}

							if (this.isObject(response.setup)) {
								this.assign(setup, response.setup);
							}

							return this.loadPage({
								name,
								setup,
								then: (page) => {
									// page construction is complete
									const title = page.getTitle();

									return { href, name, setup, title };
								},
							});
						} else if (this.isString(response.href)) {
							// page redirect
							if (response.href === href) {
								document.location.href = response.href;
							} else {
								return this.loadUrl({ href: response.href });
							}
						}
					} else {
						throw new Error(`Unsupported path for dynamic page load: ${path}`);
					}
				},
			},

			/**
			 * Sets the application mode and registers external callbacks for mobile app integration.
			 *
			 * @method Base#setMode
			 * @description
			 * Known external callbacks:
			 *  - onRequestLogin: Called when the app needs to log the user in
			 *  - onAppExitCompleted: Called when the app should exit the webview
			 *  - onUpgradeCompleted: Deprecated. Upgrades/exchanges now show OrderComplete and call onAppExitCompleted on Continue.
			 *  - onRequestPayment: Called when the app needs to process a payment
			 *  - onExitEvent: Called when the app should exit the webview from the event page (back button)
			 *  - onExitEventDashbaord: Called when the app should exit the webview from the event dashboard (back button)
			 * @example
			 * App.setMode({
			 * 	mode: 'ios',
			 * 	callbacks: {
			 * 		onRequestLogin: {
			 * 			duration: 180 // 3 minute default before auto-failing
			 * 			handler: function (callbackObject) {
			 * 				// do something
			 * 				callbackObject.success();
			 * 			},
			 * 		},
			 * 		onAppExitCompleted: {
			 * 			handler: function (callbackObject) {
			 * 				// do something
			 * 				callbackObject.success();
			 * 			}
			 * 		},
			 * 		onUpgradeCompleted: {
			 * 			handler: function (callbackObject) {
			 * 				// do something
			 * 				callbackObject.success();
			 * 			}
			 * 		},
			 * 		onRequestPayment: {
			 * 			handler: function (callbackObject) {
			 * 				// do something
			 * 				callbackObject.success();
			 * 			}
			 * 		},
			 * 		onExitEvent: {
			 * 			handler: function (callbackObject) {
			 * 				// do something
			 * 				callbackObject.success();
			 * 			}
			 * 		},
			 * 		onExitEventDashbaord: {
			 * 			handler: function (callbackObject) {
			 * 				// do something
			 * 				callbackObject.success();
			 * 			}
			 * 		}
			 * });
			 */
			setMode: {
				// NOTE: doesn't need to be public using the current implementation, injected scripts will
				// require(["App"])... App.setMode() instead of ingesting it and using this.App.setMode()
				value(options = {}) {
					const { System, container, data } = this;
					const {
						allowAnalyticsCookies = null,
						allowFunctionalCookies = null,
						allowMarketingCookies = null,
						callbacks = {},
						mode: passedMode,
						onRequestLogin,
						platform: passedPlatform,
					} = options;
					const system = System.get();
					const { platform: systemPlatform, mode: systemMode } = system;

					let mode = DEFAULT_MODE;
					let platform = DEFAULT_PLATFORM;

					if (this.isString(passedMode, 1)) {
						mode = passedMode;
					} else if (this.isString(systemMode, 1)) {
						mode = systemMode;
					}

					if (this.isString(passedPlatform, 1)) {
						platform = passedPlatform;
					} else if (this.isString(systemPlatform, 1)) {
						platform = systemPlatform;
					}

					data.mode = mode;
					container.setAttribute(`mode`, mode);

					// Need to support the old way of setting the onRequestLogin callback
					if (this.isObject(onRequestLogin)) {
						callbacks.onRequestLogin = onRequestLogin;
					}

					this.each(callbacks, (callback, name) => this.registerExternalCallback(name, callback));

					this.log(`App mode updated: %O`, { mode: mode, options });

					const { onRequestPayment } = callbacks;
					const googlePay = mode === `android` && this.isObject(onRequestPayment);

					System.setMode({
						allowAnalyticsCookies,
						allowFunctionalCookies,
						allowMarketingCookies,
						googlePay,
						mode,
						platform,
					});
				},
			},

			/**
			 * Registers an external callback
			 * @method registerExternalCallback
			 * @param {ExternalCallback} callback - The external callback to register
			 */
			registerExternalCallback: {
				value(name, callback) {
					this.log({
						priority: 10,
						message: `Registering external callback: ${name}`,
					});

					switch (name) {
						case `onRequestLogin`:
							this.defaults(callback, {
								duration: EXTERNAL_LOGIN_TIMEOUT,
							});
							break;

						default:
							break; // do nothing
					}

					this.externalCallbacks[name] = callback;
				},
			},

			/**
			 * Calls an external callback
			 * Known external callbacks:
			 * - onRequestLogin: Called when the app needs to log the user in
			 * @method callExternal
			 * @param {Object} options - The options for the external callback
			 * @param {String} options.name - The name of the external callback to call
			 * @param {Object} options.data - Any data to pass to the external callback
			 * @param {Number} options.duration - The timeout in seconds before the request is considered a failure
			 * @param {Any} options.notFound - The response if the callback is not found. If undefined, will throw an error
			 * @return {Promise} A promise that will resolve or reject when the external callback request is complete
			 */
			callExternal: {
				public: true,
				async: true,
				value(context, options) {
					const { System, externalCallbacks } = this;
					const system = System.get();
					const { isNativeApp } = system;
					const { duration, name, notFound } = options;
					const callback = externalCallbacks[name];

					if (!isNativeApp) {
						throw new Error(`Can only call external handlers in "ios" mode.`);
					}

					if (!this.isObject(callback)) {
						if (notFound === undefined) {
							throw new Error(`No external handler registered for "${name}".`);
						} else {
							return notFound;
						}
					}

					return new Promise((resolve, reject) => {
						const { handler } = callback;

						handler({
							data: options.data, // any data to pass to the handler
							success: resolve,
							fail: reject,
						});

						if (this.isInt(duration, 1)) {
							this.defer({
								then: () => reject(new Error(`Timeout calling ${name}.`)),
								duration: duration * 1000, // convert from seconds to ms
							});
						}
					});
				},
			},

			/**
			 * Checks if an external callback is available
			 * @method hasExternalCallback
			 * @param {String} name - The name of the external callback to check
			 */
			hasExternalCallback: {
				public: true,
				value(context, options) {
					const { name } = options;
					return this.isObject(this.externalCallbacks[name]);
				},
			},

			/**
			 * @method Base#exit
			 */
			exit: {
				// currently only used by iOS
				async: true,
				defaults: {
					transition: `none`,
				},
				value(options) {
					const { instances } = this;
					const { page } = instances;
					const { transition } = options;

					return page.destroy({
						// destroy it
						transition, // new page is already in place
						remove: true, // remove from the DOM
					});
				},
			},

			/**
			 * Wrapper for callExternal for onRequestLogin
			 * Makes a request to the app to log the user in. This will use the app's native login functionality.
			 * @method requestExternalLogin
			 */
			requestExternalLogin: {
				public: true,
				async: true,
				value() {
					const deferred = this.Deferred();

					this.callExternal({
						name: `onRequestLogin`,
						then: () => {
							this.require({
								deps: [`User`],
								then: () => {
									this.User.get({ force: true });
									deferred.resolve();
								},
							});
						},
						fail: (reason) => {
							const error = new Error(`Failure reported: ${reason}`);
							deferred.reject(error);
						},
					});

					return deferred.promise;
				},
			},

			/**
			 * @method Base#getConfig
			 */
			getConfig: {
				public: true,
				value(context, name) {
					const config = this.data.config;
					return this.clone(config[name]);
				},
			},

			/**
			 * @method Base#setConfig
			 */
			setConfig: {
				public: true,
				value(context, name, value) {
					const config = this.data.config;
					config[name] = value;
					return value;
				},
			},

			/**
			 * @method Base#getRequirements
			 */
			getRequirements: {
				async: true,
				value(options) {
					const { Locale, XHR } = this;
					const { path: url, queryParams } = options;
					let promise = configPromises[url];

					if (!this.isPromise(promise)) {
						promise = XHR.ajax({
							// request config from the server
							type: `GET`,
							url: `/api/page/requirements`,
							data: { url, queryParams },
						});

						configPromises[url] = promise;
					}

					return this.when({
						promise,
						then: (response) => {
							const { localeInfo, logins } = response;
							const { availableLocales, defaultLocale } = localeInfo;
							const loginsConfig = this.isArray(logins) ? logins : [`STANDARD`];

							this.setConfig(`logins`, loginsConfig);

							Locale.setAvailable({
								available: availableLocales,
								defaultKey: defaultLocale,
							});

							return response;
						},
					});
				},
			},

			/**
			 * @method Base#loadPageConfig
			 */
			loadPageConfig: {
				async: true,
				value(options) {
					let path = options.path.replace(/\/$/, ``); // remove any trailing slashes
					let promise = configPromises[path]; // use a cached promise if one is available

					if (path === ``) {
						// Landing page exception to trailing the final slash
						path = `/`;
					}

					promise = this.getRequirements({ path, queryParams: options.queryParams });

					if (!isFileSystem) {
						promise = this.when({
							promise,
							then: (pageConfig) => {
								const { Insights, Social } = this;

								Social.setPath({ path });

								return Insights.init({
									// initialize any required analytics libraries, etc.
									path,
									config: pageConfig.tracking,
									then: () => pageConfig, // chain through the page configuration
									fail: () => {
										this.log({
											type: `error`,
											message: `Error loading analytics for: ${path}`,
											priority: 10,
										});

										return pageConfig;
									},
								});
							},
						});
					}

					promise = this.when({
						promise,
						then: (pageConfig) => {
							this.data.pageConfig = pageConfig;
							return pageConfig;
						},
						fail: (reason) => {
							this.log({
								type: `error`,
								message: `Error loading page config for: ${path}`,
								priority: 10,
							});
							return {};
						},
					});

					return promise;
				},
			},

			/**
			 * @method Base#replaceState
			 */
			replaceState: {
				public: true,
				value(context, options) {
					const { history } = window;
					const setup = this.clone(options.setup);
					const { href, name, title } = options;
					const state = { href, name, setup };

					// eliminate properties that can't be serialized
					delete setup.owner;
					delete setup.container;

					// Update the current page history settings, so it has a valid first history entry
					return history.replaceState(state, title, href);
				},
			},

			/**
			 * @method Base#loadPath
			 */
			loadPath: {
				async: true,
				public: true,
				value(context, options) {
					const { history } = window;
					const { data } = this;
					const { href: rawHref } = options;

					if (!this.isString(rawHref, 1)) {
						throw new Error(`App.loadPath: options.href required`);
					}

					const { location } = document;
					const { host, protocol } = location;
					const domain = `${protocol}//${host}`;
					const regex = new RegExp(`^${domain}`);
					const href = rawHref.replace(regex, ``);
					let promise;

					data.stateChanging = true;

					promise = this.loadPageConfig({ path: href });

					promise = this.when({
						promise,
						then: () => this.loadUrl({ href }),
					});

					promise = this.when({
						promise,
						then: (state) => {
							const { href, setup, title } = state;
							const duration = 100;

							// eliminate properties that can't be serialized
							delete setup.owner;
							delete setup.container;

							history.pushState(state, title, href);
							return this.defer({ duration });
						},
					});

					promise = this.when({
						promise,
						then: () => (data.stateChanging = false), // new page loaded successfully
						fail: (reason) => {
							// error constructing page, redirect browser
							const queryParams = this.getQueryParams();
							let hrefToLoad = href;

							if (!this.isEmpty(queryParams)) {
								// there are query params to potentially append
								const { origin } = window.location;

								if (
									href.indexOf(`:`) === -1 || // no explicit protocol present
									(href[0] === `/` && href[0] !== `/`) || // no implicit protocol present
									href === origin ||
									href.indexOf(`${origin}/`) === 0 // protocol points to local site
								) {
									// local page link, do append the query params
									const encodedParams = [];
									this.each(queryParams, (value, key) => {
										if (!this.isString(key, 1)) {
											return;
										}

										const encodedKey = encodeURIComponent(key);

										if (this.isString(value) || this.isFinite(value)) {
											const encodedValue = encodeURIComponent(value);
											encodedParams.push(`${encodedKey}=${encodedValue}`);
										} else {
											encodedParams.push(`${encodedKey}`);
										}
									});
									const paramsString = encodedParams.join(`&`);
									const [path, pathParams] = href.split(`?`);

									if (this.isString(pathParams)) {
										// further split the params half of the URL to check for a hash string
										const [existingParamsString, hashString] = pathParams.split(`#`);
										const newHref = `${path}?${existingParamsString}&${paramsString}`;

										if (this.isString(hashString, 1)) {
											hrefToLoad = `${newHref}#${hashString}`;
										} else {
											hrefToLoad = newHref;
										}
									} else {
										const hrefHashSplit = href.split(`#`);
										const hashString = hrefHashSplit[1];

										if (this.isString(hashString, 1)) {
											hrefToLoad = `${path}?${paramsString}#${hashString}`;
										} else {
											hrefToLoad = `${path}?${paramsString}`;
										}
									}
								}
							}

							location.href = hrefToLoad;
						},
					});

					return promise;
				},
			},

			/**
			 * @method Base#loadPage
			 */
			loadPage: {
				async: true,
				public: true,
				defaults: {
					setup: {},
				},
				value(context, options) {
					const { DOM, data, elements, instances } = this;
					const { overlays } = instances;
					const { name, setup } = options;
					const { initialized } = data;
					const namespace = `Pages/${name}`;
					const currentPage = instances.page;
					const currentScrollPosition = elements.page.scrollTop;
					let { container } = setup;
					let isPageUpdate = false; // if the same page is being re-loaded, keep track of whether it supports updating
					let hideExisting = false;
					let promise;

					if (this.isObject(currentPage)) {
						if (currentPage.namespace === namespace && this.isFunction(currentPage.update)) {
							// this page offers live updating, use that instead
							promise = currentPage.update(setup);
							// resolve with the existing page to ensure a valid instance is available after update()
							promise = this.when({ promise, then: () => currentPage });
							container = currentPage.container;
							isPageUpdate = true;
						} else {
							// current page is about to be removed, make sure it's not taking up DOM space or appearing on top
							instances.page.container.style.cssText = `position:absolute;left:0;top:-${currentScrollPosition}px;right:0;z-index:0;`;
							hideExisting = true;
						}
					}

					if (!this.isElement(container)) {
						// a container element wasn't specified
						container = document.createElement(`div`); // create a container for the instance
						this.addClass(`Page ${name}`, container); // give it the appropriate class
						elements.page.appendChild(container);
						DOM.hide({ target: container });

						setup.container = container;
					}

					if (initialized) {
						// first page has been loaded
						// destroy the overlays in reverse (instance.destroy removes the overlay from the list)
						for (var i = overlays.length; i > 0; i--) {
							if (this.isObject(overlays[i - 1].instance)) {
								overlays[i - 1].instance.destroy();
							}
						}
					}

					// instantiate a new page
					if (!this.isPromise(promise)) {
						const newPagePromise = this.create({ namespace, setup });

						if (hideExisting) {
							const hidePromise = currentPage.hide({
								transition: `fade`,
								then: () => {
									this.scrollTo({
										// in case the previous page was scrolled
										duration: 0,
										top: 0,
									});

									this.scrollTo({
										// in case the previous page was hidden and scrolled
										container: this.elements.page,
										duration: 0,
										top: 0,
									});
								},
							});

							promise = this.when({
								promises: [newPagePromise, hidePromise],
								spread: (page) => page,
							});
						} else {
							promise = newPagePromise;
						}
					}

					promise = this.when({
						promise,
						then: (page) => {
							// the new page is ready and showing

							if (!isPageUpdate) {
								if (this.isObject(this.instances.page)) {
									// there is an existing page showing
									instances.page.destroy({
										// destroy it
										transition: `none`, // new page is already in place
										remove: true, // remove from the DOM
									});
								}

								instances.page = page; // new page has taken over
							}

							// apply the page theme to the loading spinner, to try and keep it visible
							elements.loading.setAttribute(`theme`, page.theme);

							this.defer({
								duration: 100,
								then: () => {
									// notify any instances subscribed to page changes
									this.each(data.pageChangeHandlers, (handler) => {
										const { location } = document;
										const { pathname } = location;

										handler.fn.call(handler.owner, {
											pathname,
											name,
										});
									});
								},
							});

							return page;
						},
						fin: function () {
							this.unblock({
								// unblock the site
								owner: null, // page should be ready, clear all blockers
							});

							this.tabulate();
						},
					});

					// close any applicable notifications
					this.chain(this.data.notifications).each((notification) => {
						if (notification.closeOnNavigate) {
							notification.close();
						}
					});

					return promise;
				},
			},

			/**
			 * @method Base#goBack
			 */
			goBack: {
				public: true,
				value(context) {
					const { history } = window;
					history.back();
				},
			},

			/**
			 * @method Base#loadOverlay
			 */
			loadOverlay: {
				async: true,
				public: true,
				defaults: {
					setup: {},
					replace: false, // whether to destroy all existing overlays
					hidden: false, // whether the overlay should remain hidden after loading (useful for intermediate overlays in a chain)
					skipAnimation: false, // whether to skip the CSS and JS transition animations
					transition: `fade`,
					duration: 350,
				},
				value(context, options) {
					if (!this.isString(options.type, 1)) {
						throw new Error(`App.loadOverlay: options.type is a required param`);
					}

					const { DOM, data, elements, instances } = this;
					const { setup } = options;
					const namespace = `Overlays/${options.type}`;
					const { overlays, page: currentPage } = instances;
					const { overlay: overlayContainer } = elements;
					const container = document.createElement(`div`); // used to create the new overlay
					setup.replace = options.replace; // whether to destroy all existing overlays
					const newOverlay = {
						// used to populate the list of overlays
						namespace,
						container,
						setup,
						scrollPosition: 0,
					};
					let onDestroy; // in the event that a callbacks.destroy was passed, use as a proxy
					let promise;

					// Capture the focused element before the first overlay opens so focus can be restored on close
					if (
						overlays.length === 0 &&
						this.isElement(document.activeElement) &&
						document.activeElement !== document.body
					) {
						data.overlayTriggerElement = document.activeElement;
					}

					overlays.push(newOverlay);

					if (this.isUndefined(setup.theme)) {
						if (this.isObject(context.data)) {
							setup.theme = context.data.theme;
						}
					}

					if (this.isObject(setup.callbacks)) {
						this.bind(setup.callbacks, context);
						onDestroy = setup.callbacks.destroy; // cache the potential original value for nested use
					} else {
						setup.callbacks = {};
					}

					setup.callbacks.destroy = () => {
						// run when overlay.destroy() is called, but in *this* context, not the owner's
						var existing, delay; // a promise to potentially use

						if (this.isFunction(onDestroy)) {
							onDestroy(); // trigger the original callbacks.destroy, if one was provided
						}

						this.find(overlays, (overlay, i) => {
							if (overlay === newOverlay) {
								// found the matching overlay
								overlays.splice(i, 1);
								return true; // break the loop
							}
						});

						delay = DOM.hide({
							// fade out the current overlay
							target: container,
							transition: `fade`,
							duration: options.duration,
							remove: true,
						});

						function onReady(instance) {
							// a handler for when any existing overlay is ready
							var lastOverlay = this.last(overlays);

							if (!this.isObject(lastOverlay)) {
								// something closed in the meantime, no overlays left to transition to
								return;
							}

							if (lastOverlay.container === instance.container) {
								this.removeClass(`inactive`, instance.container); // make the overlay responsive again
								this.removeClass(`scrolled-simulated`, this.get(`> .head`, instance.container));

								instance.show({
									// fade in the current one
									transition: `fade`,
									duration: options.duration,
									then: () => {
										// reset scroll position
										this.scrollTo({ top: lastOverlay.scrollPosition, duration: 0 });
									},
								}); // don't bother waiting for this to complete, continue to destructor callback below
							}
						}

						existing = this.last(overlays);
						if (this.isObject(existing)) {
							this.when({
								promises: [
									existing.promise, // the promise for the overlay to transition to
									delay,
								],
								spread: onReady,
							});
						} else {
							// no remaining overlays
							this.when({
								promise: delay,
								then: () => {
									// closing overlay has transitioned out
									existing = this.last(overlays); // make sure no overlay has been instantiated during the delay

									if (this.isObject(existing)) {
										// new overlay found
										return this.when({
											promise: existing.promise,
											then: onReady,
										});
									} else {
										return this.hide({
											target: overlayContainer,
											transition: `none`, // no transition to prevent a scroll locking issue on ios
											duration: options.duration,
											then: () => {
												this.removeClass(`vp-max-height`, elements.page); // re-enable the main page area
												this.removeClass(`overlay-active`);

												if (currentPage === instances.page) {
													// only reset scroll position if still on the same page

													if (data.pageScrollTop !== null) {
														this.scrollTo({
															top: data.pageScrollTop,
															duration: 0,
														});
													}
												}

												this.scrollTo({
													// reset scroll position of the overflow: hidden page
													container: elements.page,
													top: 0,
													duration: 0,
												});

												data.pageScrollTop = null;

												// Restore focus to the element that triggered the overlay
												if (this.isElement(data.overlayTriggerElement)) {
													data.overlayTriggerElement.focus();
													data.overlayTriggerElement = null;
												}
											},
										});
									}
								},
							});
						}
					};

					this.block({
						// display the loading spinner
						key: `loadOverlay`,
					});

					// make sure the instance hidden and ready to transition in
					setup.container = container;
					setup.show = false;
					setup.skipAnimation = options.skipAnimation;

					DOM.hide({ target: container });
					elements.overlay.appendChild(container); // NOTE: important to add at this stage, some constructors don't like being detached from the document

					promise = this.create({
						namespace,
						setup,
						then: (instance) => {
							newOverlay.instance = instance;
							return instance;
						},
						fail: (reason) => {
							// error launching the overlay
							setup.callbacks.destroy(); // manually run the destroy callback
							throw reason;
						},
					});
					newOverlay.promise = promise; // preserve a promise that will resolve with the created overlay instance

					promise = this.when({
						promise,
						then: (instance) => {
							// the new overlay is ready
							var deferred = this.Deferred();

							this.hideTooltip(); // hide any showing tooltips

							this.find(overlays, (overlay, i) => {
								if (overlay === newOverlay) {
									if (i === 0) {
										deferred.resolve(); // only overlay, resolve immediately
									}

									return true;
								} else if (options.replace) {
									// lower-level overlay, destroy
									this.when({
										promise: overlay.promise,
										then: (formerOverlay) => {
											return formerOverlay.destroy({
												transition: options.transition,
												then: () => deferred.resolve(),
											});
										},
									});
								} else {
									// lower-level overlay, hide
									this.when({
										promise: overlay.promise,
										then: (formerOverlay) => {
											const OFFSET_AMOUNT = 15;
											overlay.scrollPosition = data.scrollTop;

											this.addClass(`inactive`, formerOverlay.container); // make the overlay unresponsive

											formerOverlay.container.scrollTo({
												top: overlay.scrollPosition,
												behavior: `instant`,
											});

											if (overlay.scrollPosition > OFFSET_AMOUNT) {
												this.addClass(`scrolled-simulated`, this.get(`> .head`, formerOverlay.container));
											}

											deferred.resolve();
										},
									});
								}
							});

							this.defer({
								duration: 1000, // emergency failsafe
								then: () => {
									deferred.resolve(); // avoid waiting forever
								},
							});

							return this.when({
								promise: deferred.promise,
							});
						},
					});

					promise = this.when({
						promise,
						then: () => {
							const pageContainer = this.elements.page;
							const scrollTop = window.scrollY;

							if (this.last(overlays) === newOverlay) {
								// still the top level, proceed
								this.addClass(`vp-max-height`, pageContainer);
								this.addClass(`overlay-active`);

								if (this.Utils.first(overlays) === newOverlay) {
									// only overlay present

									this.scrollTo({
										// apply the scroll position to the overflow: hidden page
										container: pageContainer,
										top: scrollTop,
										duration: 0,
									});

									this.data.pageScrollTop = scrollTop; // save the position to use for transitioning back to page
								}

								this.scrollTo({
									// reset to 0 scroll for the new overlay
									duration: 0,
									top: newOverlay.scrollPosition,
								});

								if (options.hidden) {
									// keep the overlay container visible but don't show the overlay instance itself
									return this.when({
										promises: [
											DOM.show({
												transition: `none`,
												duration: 0,
												target: overlayContainer,
											}),
										],
										fin: this.tabulate,
									});
								}

								// show both in parallel to be a little more responsive to scrolling on mobile
								return this.when({
									promises: [
										newOverlay.instance.show({
											transition: options.transition,
											duration: options.duration,
										}),
										DOM.show({
											transition: options.transition,
											duration: options.duration,
											target: overlayContainer,
										}),
									],
									fin: this.tabulate,
								});
							}
						},
						fin: () => {
							this.unblock({
								// unblock the site
								owner: null, // should be ready, clear all blockers
							});
						},
					});

					// close any applicable notifications
					this.chain(this.data.notifications).each((notification) => {
						if (notification.closeOnNavigate) {
							notification.close();
						}
					});

					return this.when({
						promise,
						then: () => newOverlay.instance,
					});
				},
			},

			/**
			 * @method Base#adjustOverlay
			 * @param {Object} [options]
			 * @param {string} [options.namespace] - If passed, will seek the first matching overlay
			 * @param {number} [options.scrollPosition] - The updated overlay scroll position value
			 */
			adjustOverlay: {
				public: true,
				defaults: {
					namespace: null,
					scrollPosition: null,
				},
				value(context, options) {
					const { instances } = this;
					const { overlays } = instances;
					const { namespace, scrollPosition } = options;
					let overlay;

					if (namespace === null) {
						// default to the last launched overlay
						overlay = this.last(overlays);
					} else {
						// namespace was passed, find the first matching instance
						overlay = this.find(overlays, { namespace });
					}

					if (this.isObject(overlay)) {
						// matching overlay found
						if (Number.isFinite(scrollPosition)) {
							// scroll position was passed
							overlay.scrollPosition = scrollPosition;
						}
					}
				},
			},

			/**
			 * @method Base#notifyUser
			 */
			notifyUser: {
				async: true,
				public: true,
				defaults: {
					type: `message`, // message | success | warning | error
					closeOnNavigate: false, // whether to auto-close on page or overlay navigation
					theme: `LIGHT`, // default theme for the notification (Dark theme for white toast)
				},
				value(context, options) {
					return this.require({
						deps: [`Templates`],
						then: () => {
							const { Templates } = this;
							const { getIcon } = Templates;
							const VALID_TYPES = [`success`, `message`, `warning`, `error`];
							const deferred = this.Deferred();
							const element = document.createElement(`li`);
							let notification;
							let icon = options.icon;

							if (!this.isString(icon, 1)) {
								switch (options.type) {
									case `error`:
										icon = getIcon(`exclamation-mark-circled`);
										break;
									case `warning`:
										icon = getIcon(`exclamation-mark-triangle`);
										break;
									case `success`:
										icon = getIcon(`checkmark-circled`);
										break;
									default:
										icon = getIcon(`moreinfo-unfilled`);
								}
							} else {
								icon = getIcon(icon);
							}

							const errorContents = options.type === `error` ? `<h2>Error</h2>` : ``;
							const contents =
								`<div class="content">${icon}<div class="info">${errorContents}<p class="message">${options.message}</p></div></div>` +
								getIcon(`close`);
							const matching = this.filter(this.data.notifications, {
								contents,
								type: options.type,
							});

							if (!this.includes(VALID_TYPES, options.type)) {
								throw new Error(`App.notifyUser: Unrecognized options.type "${options.type}"`);
							}

							if (this.isUndefined(options.duration)) {
								switch (options.type) {
									case `error`:
									case `warning`:
										options.duration = 8000;
										break;
									default:
										options.duration = 4000;
								}
							}

							this.each(matching, (notification) => {
								this.DOM.hide({
									target: notification.element,
									transition: `height`,
								});
							});

							element.className = options.type;
							element.innerHTML = contents;
							element.style.display = `none`;
							element.setAttribute(`theme`, options.theme);

							notification = {
								element,
								contents,
								type: options.type,
								deferred,
								closeOnNavigate: options.closeOnNavigate,
								close: this.bind(function () {
									this.DOM.trigger({
										event: `click`,
										selector: `.icon-close`,
										container: element,
									});
								}),
							};

							this.elements.notifyContainer.appendChild(element);
							this.data.notifications.push(notification);

							this.DOM.show({
								target: element,
								transition: `height`,
								then: () => {
									if (options.duration > 0) {
										this.defer({
											duration: options.duration,
											then: notification.close,
										});
									}
								},
							});

							return deferred.promise;
						},
					});
				},
			},

			/**
			 * @method Base#spawnTooltip
			 */
			spawnTooltip: {
				async: true,
				public: true,
				defaults: {
					type: `message`,
					direction: `top`,
					cushion: 0,
					duration: null,
				},
				value(context, options) {
					if (!this.isElement(options.target)) {
						options.target = context.container;
					}

					const data = this.data;

					const existing = this.find(data.tooltips, (tooltip) => {
						// iterate over all the tooltips
						if (!tooltip.instance.alive) {
							this.log({
								priority: 10,
								type: `warning`,
								message: `Base: Dead tooltip in the array.`,
							});
						} else if (tooltip.owner === context) {
							// same creator
							if (tooltip.target === options.target && tooltip.direction === options.direction) {
								// same target and direction
								tooltip.instance.update({
									// update the content and return
									message: options.message,
									image: options.image,
									type: options.type,
									direction: options.direction,
								});
								return true;
							}
						}
					});

					let promise;

					if (!this.isObject(existing)) {
						let instance;

						promise = this.create({
							namespace: `Modules/Tooltip`,
							setup: {
								hide: true,
								show: false, // don't transition in
								type: options.type,
								message: options.message,
								direction: options.direction,
								image: options.image,
								callbacks: {
									die: () => {
										this.extract(data.tooltips, {
											// remove from the array of tooltips
											instance,
										});
									},
								},
							},
							then: (tooltip) => {
								instance = tooltip;
								this.elements.tooltips.appendChild(instance.container);

								data.tooltips.push({
									// save a reference to the tooltip
									owner: context,
									target: options.target,
									instance,
									type: options.type,
									message: options.message,
									direction: options.direction,
									cushion: options.cushion,
									hover: options.hover,
								});

								instance.show({
									// reveal the tooltip
									transition: data.tooltipTransitionIn,
								});

								if (this.isInt(options.duration)) {
									this.defer({
										duration: options.duration,
										then: () =>
											instance.destroy({
												transition: data.tooltipTransitionOut,
											}),
									});
								}

								if (this.isElement(options.hover)) {
									// a 'hover' element was provided, hide on mouse exit
									if (!data.mouseTrackingEnabled) {
										// make sure we're not already listening
										const onMouseOut = function (evt) {
											var activeTooltip = false; // track whether any tooltips still need the listener

											this.extract(data.tooltips, (tooltip) => {
												if (!tooltip.instance.alive) {
													return true; // filter out immediately
												} else if (this.isElement(tooltip.hover)) {
													// this is a 'hover' tooltip
													if (!this.DOM.containsElement(evt.toElement, tooltip.hover)) {
														// target is not the hover target
														tooltip.instance.destroy({
															// remove the tooltip
															transition: data.tooltipTransitionOut,
															remove: true,
														});

														return true; // filter out this tooltip
													} else {
														activeTooltip = true;
													}
												}
											});

											if (!activeTooltip) {
												// no need to keep listening
												this.DOM.off({
													target: document.body,
													event: `mouseout`,
													handler: onMouseOut,
												}); // remove this listener
											}
										};

										this.DOM.on({
											target: document.body,
											event: `mouseout`,
											matchContainer: false,
											handler: onMouseOut,
										});
									}
								}

								return instance;
							},
						});
					}

					return this.when({
						// defer for tooltip creation if necessary
						promise,
						then: this.positionTooltips,
					});
				},
			},

			/**
			 * @method Base#hideTooltip
			 */
			hideTooltip: {
				public: true,
				defaults: {},
				value(context, options) {
					var query = {};

					if (this.isObject(options.owner)) {
						query.owner = options.owner;
					} else if (context !== this) {
						// only internal calls are allowed to hide all other owners' tooltips
						query.owner = context;
					}

					if (this.isElement(options.target)) {
						query.target = options.target;
					}

					this.chain(this.data.tooltips)
						.filter(query)
						.each((tooltip) => {
							tooltip.instance.destroy({
								// destroy any tooltips that match the query
								transition: this.data.tooltipTransitionOut,
								remove: true,
							});
						});
				},
			},

			/**
			 * @method Base#positionTooltips
			 * Public so Utils/KeyboardViewport can re-place tooltips when the iOS keyboard moves a sheet
			 * (that fires visualViewport events, not the window resize this otherwise follows).
			 */
			positionTooltips: {
				public: true,
				value() {
					const { DOM, data, elements } = this;
					let tooltipElements = DOM.select(`.Tooltip`, elements.tooltips);

					this.each(data.tooltips, (tooltip) => {
						const { instance, target } = tooltip;
						const { container } = instance;
						const targetDimensions = DOM.getDimensions(target);
						const tooltipDimensions = DOM.getDimensions(container);
						const tooltipsContainerElement = this.get(`#tooltips`, document.body);
						const tooltipsContainerDimensions = DOM.getDimensions(tooltipsContainerElement);
						let midWidth;
						let midHeight;
						let left;
						let top;

						tooltipElements = this.without(tooltipElements, container);

						if (!targetDimensions.isVisible) {
							// discard any old tooltips
							return instance.destroy();
						}

						midWidth = targetDimensions.viewportLeft + 0.5 * targetDimensions.width;
						midHeight = targetDimensions.viewportTop + 0.5 * targetDimensions.height;

						switch (tooltip.direction) {
							case `top`:
								left = midWidth - tooltipDimensions.width / 2;
								top =
									targetDimensions.viewportTop -
									tooltipDimensions.height -
									tooltipsContainerDimensions.viewportTop -
									tooltip.cushion;
								break;
							case `center`:
								left = midWidth - tooltipDimensions.width / 2;
								top = targetDimensions.viewportTop + targetDimensions.height / 2 - tooltipDimensions.height;
								break;
							case `right`:
								left = targetDimensions.viewportLeft + targetDimensions.width + tooltip.cushion;
								top = midHeight - tooltipDimensions.height / 2;
								break;
							case `bottom`:
								left = midWidth - tooltipDimensions.width / 2;
								top = targetDimensions.viewportTop + targetDimensions.height + tooltip.cushion;
								break;
							case `left`:
								left = targetDimensions.viewportLeft - tooltipDimensions.width - tooltip.cushion;
								top = midHeight - tooltipDimensions.height / 2;
								break;
						}

						container.style.left = Math.floor(left) + `px`;
						container.style.top = Math.floor(top) + `px`;
					});

					this.each(tooltipElements, (element) => {
						// iterate over any tooltip elements not found in the existing instances
						const priority = 5;
						const type = `warning`;
						const message = `lingering tooltip removed`;

						this.log({ message, priority, type });

						if (this.isElement(element)) {
							DOM.remove(element);
						}
					});
				},
			},

			/**
			 * @method Base#offPageChange
			 */
			offPageChange: {
				public: true,
				value(context, options) {
					const { data } = this;
					const len = data.pageChangeHandlers.length;
					let removed;
					let owner;
					let fn;

					if (this.isObject(options)) {
						fn = options.fn;
						owner = options.owner || context;
					} else if (this.isFunction(options)) {
						fn = options;
						owner = context;
					} else {
						throw new Error(`App.offPageChange: no valid options passed`);
					}

					if (this.isFunction(fn)) {
						this.extract(data.pageChangeHandlers, { owner, fn });
					} else {
						// clear all listeners from this owner
						this.extract(data.pageChangeHandlers, { owner });
					}
					removed = data.pageChangeHandlers.length < len;
					return removed; // return true if a listener was removed
				},
			},

			/**
			 * @method Base#block
			 */
			block: {
				public: true,
				defaults: {},
				value(context, options) {
					// A process is requesting to block the app, e.g. show a loading spinner during XHR requests

					const { DOM, data, elements } = this;
					const { blockers } = data;
					const { loading: loadingLayer } = elements;
					const { key, message, owner } = options;
					const base = { key, message, owner };
					const blocker = this.defaults(base, {
						owner: context,
						key: null,
					});

					let blockerNamespace;
					let promise;

					blockers.push(blocker);

					blockerNamespace = blocker.owner.namespace;
					this.log({
						priority: 10,
						message: `++ Blocker added (${blockerNamespace}) ${blocker.key}`,
					});

					promise = this.defer({
						duration: 50,
						then: () => {
							if (blockers.length > 0) {
								return this.DOM.show({
									// show it, to block user interaction
									target: loadingLayer,
									transition: `fade`,
									duration: 200,
								});
							}
						},
					});

					this.when({
						// show it, to block user interaction
						promise,
						then: () => {
							if (blockers.length > 0) {
								// still blocked
								const message = data.blockers[0].message; // optional text to display
								let messageElement = document.getElementById(`loading-message`); // reference to the message text element

								// avoid breaking unit tests, if expected global elements aren't there
								if (this.isElement(messageElement)) {
									if (this.isString(message, 1)) {
										// A message was passed along
										messageElement.innerHTML = message;

										DOM.show({
											target: messageElement,
											transition: `fade`,
										});
									} else {
										// no message, hide
										messageElement.innerHTML = ``;
									}
								}
							} else {
								// no blockers, abort
								DOM.hide({
									target: loadingLayer,
									transition: `none`,
								});
							}
						},
					});
				},
			},

			/**
			 * @method Base#unblock
			 */
			unblock: {
				public: true,
				defaults: {},
				value(context, options) {
					const loadingLayer = this.elements.loading;
					let keyLabel = `*`;
					let query = {};
					let ownerLabel;

					if (this.isObject(options.owner)) {
						query.owner = options.owner;
						ownerLabel = `(${query.owner.namespace}) `;
					} else if (this.isNull(options.owner) || !this.isObject(context)) {
						// null intentionally skips
						ownerLabel = ``;
					} else {
						// context must be an object
						query.owner = context;
						ownerLabel = `(${context.namespace}) `;
					}

					if (this.isString(options.key, 1)) {
						query.key = options.key;
						keyLabel = query.key;
					} else {
						keyLabel = ``;
					}

					this.extract(this.data.blockers, query);

					this.log({
						priority: 10,
						message: `-- Blocker removed ${ownerLabel} ${keyLabel}`,
					});

					this.defer({
						duration: 50,
						then: () => {
							if (this.data.blockers.length === 0) {
								return this.DOM.hide({
									target: loadingLayer,
									transition: `fade`,
								});
							}
						},
					});
				},
			},

			/**
			 * @method Base#setQueryString
			 */
			setQueryString: {
				public: true,
				value(context, value) {
					const { data } = this;
					const paramString = value.replace(/^\?/, ``);
					const valuePairs = paramString.split(`&`);

					data.queryString = paramString;
					data.queryParams = {};

					if (paramString === ``) {
						return;
					}

					this.each(valuePairs, (valuePair) => {
						const split = valuePair.split(`=`);
						const [rawKey, rawValue] = split;
						const key = decodeURIComponent(rawKey);
						const value = this.isString(rawValue) ? decodeURIComponent(rawValue) : null;

						data.queryParams[key] = value;
					});
				},
			},

			/**
			 * @method Base#getQueryString
			 */
			getQueryString: {
				public: true,
				value(context) {
					return this.data.queryString;
				},
			},

			/**
			 * @method Base#getQueryParams
			 */
			getQueryParams: {
				clone: true,
				public: true,
				value(context) {
					return this.data.queryParams;
				},
			},

			/**
			 * @method Base#getSavedQueryParam
			 */
			getSavedQueryParam: {
				public: true,
				value(context, key) {
					const { data } = this;
					const { savedQueryParams } = data;
					const match = savedQueryParams[key];

					if (this.isObject(match)) {
						return match.value;
					}
				},
			},

			/**
			 * @method Base#setQueryParam
			 */
			setQueryParam: {
				private: false,
				public: true,
				value(context, name, value, updateUrl = false) {
					var queryParams = this.data.queryParams;

					name = name.toUpperCase();

					this.each(queryParams, (val, key) => {
						if (key.toUpperCase() === name) {
							if (this.isNull(value)) {
								delete queryParams[key];
							} else if (this.isString(name)) {
								queryParams[key] = value;
							} else {
								throw new Error(`App.setUrlParam(key, value): value must be string or null.`);
							}
						}
					});

					if (updateUrl) {
						const { hash: hashParams, pathname: path } = location;
						const params = this.serializeParams(queryParams);
						const url = this.isString(params, 1)
							? `${path}?${this.serializeParams(queryParams)}${hashParams}`
							: `${path}${hashParams}`;
						window.history.replaceState(null, ``, url);
					}
				},
			},

			/**
			 * @method Base#setHashString
			 */
			setHashString: {
				value(value) {
					const { data } = this;
					const paramString = value.replace(/^#/, ``);
					const valuePairs = paramString.split(`&`);

					data.hashString = paramString;
					data.hashParams = {};

					if (paramString === ``) {
						return;
					}

					this.each(valuePairs, (valuePair) => {
						const split = valuePair.split(`=`);
						const [rawKey, rawValue] = split;
						const key = decodeURIComponent(rawKey);
						const value = this.isString(rawValue) ? decodeURIComponent(rawValue) : null;

						data.hashParams[key] = value;
					});
				},
			},

			/**
			 * @method Base#getHashString
			 */
			getHashString: {
				public: true,
				value(context) {
					return this.data.hashString;
				},
			},

			/**
			 * @method Base#getHashParams
			 */
			getHashParams: {
				clone: true,
				public: true,
				value(context) {
					return this.data.hashParams;
				},
			},

			/**
			 * @method Base#onScroll
			 */
			onScroll: {
				public: true,
				value(context, arg, fireImmediately = false) {
					// insert a new handler into the list of things to call onScroll
					const { data } = this;
					const { scrollHandlers, scrollTop: cachedScrollTop } = data;
					const scrollTop = this.isFinite(cachedScrollTop) ? cachedScrollTop : 0;
					const scrollDelta = 0;
					const details = { scrollDelta, scrollTop };
					let config;

					if (this.isFunction(arg)) {
						// called via this.onScroll(fn) or this.App.onScroll(fn)
						config = {
							owner: context,
							fn: arg,
						};
					} else if (this.isObject(arg)) {
						// called via this.onScroll(options), to allow for a custom owner
						config = this.defaults(arg, {
							owner: context, // match default behavior if no override is present
						});
					}

					// add to the list of subscribers
					scrollHandlers.push(config);

					if (fireImmediately) {
						this.defer({
							// simulate a scroll event with cached details
							then: () => config.fn.call(config.owner, details),
						});
					}
				},
			},

			/**
			 * @method Base#offScroll
			 */
			offScroll: {
				public: true,
				value(context, arg) {
					// remove the requested handlers from the onScroll subscriber list
					let filter = {};

					if (this.isFunction(arg)) {
						filter.fn = arg;
						filter.owner = context; // default to self
					} else if (this.isObject(arg)) {
						filter.owner = this.isObject(arg.owner) ? arg.owner : context;

						if (this.isFunction(arg.fn)) {
							filter.fn = arg.fn;
						}
					}

					this.extract(this.data.scrollHandlers, filter);
				},
			},

			/**
			 * Allow modules like timers to subscribe to one-second interval timers
			 * @method Base#onTick
			 */
			onTick: {
				public: true,
				value(context, options) {
					if (this.isFunction(options)) {
						options = {
							owner: context,
							fn: options,
						};
					} else if (this.isObject(options)) {
						this.defaults(options, {
							owner: context,
						});
					} else {
						throw new Error(`App.onTick(): Invalid argument, expected Function | Object.`);
					}

					this.data.tickHandlers.push({
						owner: options.owner,
						fn: options.fn,
					});

					if (!this.isInt(this.data.tickIntervalId)) {
						// set up the interval timer, if it doesn't already exist
						this.data.tickIntervalId = window.setInterval(this.onIntervalTimeout, 1000);
					}
				},
			},

			/**
			 * @method Base#offTick
			 */
			offTick: {
				public: true,
				defaults: {},
				value(context, options) {
					let query = {};

					if (this.isFunction(options)) {
						query.fn = options;
						query.owner = context;
					} else if (this.isUndefined(options.owner)) {
						// default handling
						query.owner = context;
					} else if (!this.isNull(options.owner)) {
						// a non-null value was passed
						query.owner = options.owner;
					} // null explicitly passed

					if (this.isFunction(options.fn)) {
						query.fn = options.fn;
					}

					this.extract(this.data.tickHandlers, query);

					if (this.isInt(this.data.tickIntervalId) && this.data.tickHandlers.length === 0) {
						// remove the interval timer, if nobody's listening
						window.clearInterval(this.data.tickIntervalId);
						this.data.tickIntervalId = null;
					}
				},
			},

			/**
			 * @method Base#getCookie
			 */
			getCookie: {
				public: true,
				value(context, name) {
					const cookieArray = document.cookie.split(`;`);

					for (let i = cookieArray.length - 1; i >= 0; i--) {
						let cookie = cookieArray[i];
						let splitIndex = cookie.indexOf(`=`);
						let key = this.trim(cookie.substring(0, splitIndex));
						if (key === name) {
							let value = cookie.substring(splitIndex + 1);
							return decodeURIComponent(value);
						}
					}
					return null;
				},
			},

			/**
			 * @method Base#getCookiesByPattern
			 * @returns {Record<string, string>}
			 * @example this.getCookiesByPattern(`^queue-it-`) // {"queue-it-1": "id=abc", "queue-it-2": "id=xyz"}
			 */
			getCookiesByPattern: {
				public: true,
				value(context, pattern) {
					const regex = new RegExp(pattern);
					const matches = {};
					const cookieArray = document.cookie.split(`;`);

					for (let i = cookieArray.length - 1; i >= 0; i--) {
						let cookie = cookieArray[i].trim();
						let splitIndex = cookie.indexOf(`=`);
						let key = cookie.substring(0, splitIndex);

						if (regex.test(key)) {
							let value = cookie.substring(splitIndex + 1);
							matches[key] = decodeURIComponent(value);
						}
					}

					return matches;
				},
			},

			/**
			 * @method Base#setCookie
			 */
			setCookie: {
				public: true,
				value(context, name, value, daysToExpire = 3) {
					const { location } = window;
					const { hostname } = location;
					let cookieValue = `${value};`;

					if (daysToExpire > 0) {
						// value 0 is for a session cookie
						const expirationDate = new Date();
						const expirationDay = expirationDate.getDate() + daysToExpire;
						expirationDate.setDate(expirationDay); // push the date into the future
						const expirationString = expirationDate.toUTCString(); // format UTC string

						cookieValue += `expires=${expirationString};`;
					}
					cookieValue += `domain=.${hostname};`; // set for the current domain
					document.cookie = `${name}=${cookieValue};path=/;`;
				},
			},

			/**
			 * @method Base#deleteCookie
			 */
			deleteCookie: {
				public: true,
				value(context, name) {
					const { location } = window;
					const { hostname } = location;
					const currentDate = new Date();
					const timestamp = currentDate.toUTCString();

					// set a blank value that immediately expires
					// TODO: get on the same page as BE when setting cookies, so we can focus on a single approach
					document.cookie = `${name}=;domain=.${hostname};expires=${timestamp};path=/`; // dot-prefixed "domain-wide"
					document.cookie = `${name}=;domain=${hostname};expires=${timestamp};path=/`; // non-prefixed "domain-wide"
					document.cookie = `${name}=;expires=${timestamp};path=/`; // no domain, "host-only"
				},
			},

			setFirstPartyStorage: {
				public: true,
				value(context, options) {
					const { Storage, data } = this;
					const { userEmailHash, userId } = options;
					const { localStorageExpiration: expiration, localStorageKey } = data;

					if (!this.isString(userEmailHash, 1) || !this.isString(userId, 1)) {
						throw new Error(`Missing required userEmailHash and userId.`);
					}

					// update the internal state variables
					this.assign(data, { savedUserEmailHash: userEmailHash, savedUserId: userId });

					// store the values into Local Storage
					const value = JSON.stringify({ expiration, userEmailHash, userId });
					const key = localStorageKey;

					Storage.set({ key, value });
				},
			},
			clearFirstPartyStorage: {
				public: true,
				value(context, options) {
					const { Storage, data } = this;
					const { localStorageKey } = data;

					this.assign(data, { savedUserEmailHash: null, savedUserId: null });
					Storage.remove({ key: localStorageKey });
				},
			},
			getFirstPartyStorage: {
				public: true,
				value(context, options) {
					const { Storage, Time, data } = this;
					const { localStorageKey } = data;
					const currentTime = Time.getTime();

					try {
						let firstPartyStorageString = Storage.get({ key: localStorageKey });

						// TODO: remove in 30 days, when all cookies have expired
						if (!this.isString(firstPartyStorageString, 1)) {
							firstPartyStorageString = this.getCookie(localStorageKey);
						}
						// END TODO

						if (!this.isString(firstPartyStorageString, 1)) {
							throw new Error(`First Party storage is not set`);
						}

						const result = JSON.parse(firstPartyStorageString);
						const { expiration } = result;

						if (this.isFinite(expiration) && expiration < currentTime) {
							throw new Error(`First Party storage has expired`);
						}

						return result;
					} catch (error) {
						// swallow error, return empty result
						return {};
					}
				},
			},

			getSavedQueryParams: {
				value() {
					const { Storage, data } = this;
					const { savedQueryParamsKey } = data;

					try {
						// try to get the saved query params from Local Storage
						let savedQueryParamsString = Storage.get({ key: savedQueryParamsKey });

						// TODO: remove after this has been live 30 days, when all cookies have expired
						if (!this.isString(savedQueryParamsString, 1)) {
							savedQueryParamsString = this.getCookie(savedQueryParamsKey);
						}
						// END TODO

						if (!this.isString(savedQueryParamsString, 1)) {
							throw new Error(`Saved query params not found`);
						}

						const result = JSON.parse(savedQueryParamsString);

						return result;
					} catch (error) {
						// swallow error, return empty result
						return {};
					}
				},
			},

			/**
			 * @method Base#popup
			 */
			popup: {
				public: true,
				defaults: {
					name: `_blank`,
					scrollbars: true,
					toolbar: true,
					location: true,
					resizable: true,
				},
				value(context, options) {
					const params = [
						`scrollbars=` + (options.scrollbars ? `yes` : `no`),
						`toolbar=` + (options.toolbar ? `yes` : `no`),
						`location=` + (options.location ? `yes` : `no`),
						`resizable=` + (options.resizable ? `yes` : `no`),
					];

					if (this.isInt(options.width, 0)) {
						params.push(`width=${options.width}`);
					}

					if (this.isInt(options.height, 0)) {
						params.push(`height=${options.height}`);
					}

					window.open(options.url, options.name, params.join(`,`));
				},
			},

			/**
			 * @method Base#getPath
			 */
			getPath: {
				public: true,
				value(context, str) {
					let replaceRegex = /^[^/]*\/\/[^/]+/; // Regex for "remove everything before the first slash, after a double slash + text"
					if (!this.isString(str)) {
						str = window.location.pathname;
					}

					return str.replace(replaceRegex, ``);
				},
			},

			/**
			 * @method Base#parseError
			 */
			parseError: {
				public: true,
				value(context, error) {
					var returnError = null;
					if (this.isObject(error) && this.isString(error.responseText)) {
						try {
							returnError = JSON.parse(error.responseText);
						} catch (err) {
							returnError = error;
							// throw new Error(err);
						}
					}
					return returnError;
				},
			},

			/**
			 * @method Base#onIntervalTimeout
			 */
			onIntervalTimeout: {
				value() {
					// Called on a 1000ms interval, whenever there are instances subscribed
					const { data } = this;

					this.extract(data.tickHandlers, ({ fn, owner }) => {
						if (!owner.alive) {
							return true;
						}

						fn.call(owner); // trigger each of the onTick handlers, using its owners context
					});
				},
			},

			/**
			 * @member {string} Base.friendlyUl
			 */
			friendlyUrl: {
				static: true,
				public: true,
				value(string) {
					string = string.replace(/[^a-z0-9/]+/gi, `-`); // replace any invalid characters with collapsed '-' characters
					string = string.replace(/(^-*|-*$)/g, ``); // remove any that start or end the result
					return string.toLowerCase();
				},
			},

			/**
			 * Base#askUser
			 */
			askUser: {
				public: true,
				async: true,
				value(context, options) {
					let deferred = this.Deferred();

					options = this.defaults(options, {
						yesLabel: `Yes`,
						noLabel: `No`,
						overlayMode: `overlay`,
						addHead: true,
						includeHeaderIcon: false,
					});

					if (this.isUndefined(options.theme)) {
						if (this.isObject(context.data)) {
							options.theme = context.data.theme;
						}
					}

					this.loadOverlay({
						type: `Message`,
						owner: context,
						setup: {
							addHead: options.addHead,
							confirmationPrompt: options.confirmationPrompt,
							confirmationText: options.confirmationText,
							heading: options.heading,
							theme: options.theme,
							type: `confirmation`,
							message: options.message,
							yesLabel: options.yesLabel,
							noLabel: options.noLabel,
							overlayMode: options.overlayMode,
							style: options.style,
							mobileModalStyle: `floating`,
							includeHeaderIcon: options.includeHeaderIcon,
							size: options.size,
							callbacks: {
								success: function (result) {
									deferred.resolve(result);
								},
								die: function () {
									// assume the user closed the overlay without answering
									deferred.resolve(false);
								},
							},
						},
						defer: false, // don't wait for the currently overlay to end, ask immediately
						fail: (reason) => deferred.reject(reason), // inject any errors into the promise chain
					});

					return deferred.promise;
				},
			},

			/**
			 * Base#useWaitingRoom
			 */
			useWaitingRoom: {
				public: true,
				value(context, options) {
					const deferred = this.Deferred();
					const uniqueId = this.uniqueId();
					const key = `waitroom-${uniqueId}`;
					const {
						duration = 10000, // how long to wait before instantiating the overlay
						onCancel,
						promise,
						isOpen = true,
					} = options;
					const result = { promise: deferred.promise };
					let { button, heading, message } = options;
					let resolved = false;
					let overlayPromise;

					// immediately block user interaction
					this.block({ key });

					this.when({
						promise,
						then: (result) => deferred.resolve(result),
						fail: (reason) => deferred.reject(reason),
						fin: () => {
							resolved = true;

							if (this.isPromise(overlayPromise)) {
								this.when({
									promise: overlayPromise,
									then: (overlay) => overlay.destroy(),
								});
							}
						},
					});

					const spawnOverlay = () => {
						if (resolved || this.isPromise(overlayPromise)) {
							return;
						}

						overlayPromise = this.loadOverlay({
							type: `WaitingRoom`,
							setup: {
								button,
								heading,
								message,
								theme: options.theme,
								isOpen,
								callbacks: {
									die: () => {
										if (!resolved) {
											const error = new Error(`User exited waitroom.`);

											if (this.isFunction(onCancel)) {
												// fire the callback passed by the original caller
												onCancel.call(context);
											}

											// fail the returned promise
											deferred.reject(error);
										}
									},
								},
							},
							// Unblock user interaction once the overlay is showing
							fin: () => this.unblock({ key }),
						});
					};

					this.defer({
						duration,
						then: () => {
							if (resolved) {
								// already resolved, unblock immediately
								this.unblock({ key });
							} else {
								spawnOverlay();
							}
						},
					});

					result.update = (config) => {
						if (this.isObject(config) && this.isString(config.heading)) {
							heading = config.heading;
						}

						if (this.isObject(config) && this.isString(config.message)) {
							message = config.message;
						}

						if (this.isObject(config) && this.isString(config.button)) {
							button = config.button;
						}

						if (this.isPromise(overlayPromise)) {
							// overlay has been instantiated
							this.when({
								promise: overlayPromise,
								then: (overlay) => {
									if (this.isObject(overlay)) {
										// pass the update to the overlay
										overlay.update(config);
									}
								},
							});
						} else {
							// backend returned a delay — spawn overlay immediately
							spawnOverlay();
						}
					};

					return result;
				},
			},

			/**
			 * @method Base#triggerScroll
			 */
			triggerScroll: {
				public: true,
				value(context) {
					const { DOM, elements } = this;
					const { site: target } = elements;
					const event = `scroll`;

					DOM.trigger({ event, target });
				},
			},

			/**
			 * For scrolling something into the user's viewports
			 * @method Base#scrollTo
			 */
			scrollTo: {
				private: true,
				public: true,
				async: true,
				value(context, options) {
					options = this.defaults(options, {
						owner: context,
						top: null,
						target: context.container,
						duration: SCROLL_DURATION,
						buffer: 100,
						easing: `ease-in-out`,
					});

					const duration = options.duration;
					const deferred = this.Deferred();
					const target = options.target;
					let viewport = options.container;
					let viewportDimensions;
					let targetDimensions;
					let scrollTop;

					if (!this.isElement(viewport)) {
						// no specific viewport was passed
						if (!this.DOM.containsElement(target, document.body)) {
							throw new Error(`Invalid target provided for Base.scrollTo(options)`);
						}

						viewport = document.documentElement;
					}

					if (this.isFinite(options.top)) {
						// a specific position was specified
						scrollTop = options.top;
					} else if (this.isElement(target)) {
						viewportDimensions = this.DOM.getDimensions(viewport);
						targetDimensions = this.DOM.getDimensions(target);

						if (viewportDimensions.top < targetDimensions.top && viewportDimensions.bottom > targetDimensions.bottom) {
							// already within viewport
							this.triggerScroll();
							return; // skip anything else
						}

						/**
						 * Calculate what viewport.scrollTop should be....
						 *
						 * X: distance from screen top to target top, when viewport not scrolled: target.top + viewport.scrollTop
						 * Y: distance from screen top to viewport top: viewport.top
						 * Z: amount viewport should be scrolled
						 * b: how many px below viewport.top target should appear, 0 + buffer
						 *
						 * X - Y - Z = b
						 * (target.top + viewport.scrollTop) - (viewport.top) - (scrollTop) = (buffer)
						 * (scrollTop) = (target.top + viewport.scrollTop) - (buffer)
						 **/
						scrollTop = targetDimensions.viewportTop + viewport.scrollTop - options.buffer;
					} else {
						throw new Error(`App.scrollTo(): options.top<Number> or options.target<DOMElement> required.`);
					}

					if (duration === 0) {
						viewport.scrollTop = scrollTop;
						deferred.resolve();
					} else {
						jQuery(viewport).animate(
							{
								duration,
								easing: options.easing,
								scrollTop,
							},
							() => {
								deferred.resolve();
								this.triggerScroll();
							}
						);
					}

					return deferred.promise;
				},
			},

			/**
			 * @method Base#getSubdomain
			 */
			getSubdomain: {
				static: true,
				private: false,
				public: true,
				value() {
					const myLocation = window.location.pathname; //return:   /groups/group-subdomain/events/event-name-id   old: /subdomain/event/event-name/id or something like that
					let subdomain = myLocation.split(`/`)[1]; // extract the first substring after the initial "/" to account for legacy formats

					if (subdomain === `groups`) {
						subdomain = myLocation.split(`/`)[2]; // extract the second substring after initial "/" to account for new format
					}

					return subdomain;
				},
			},

			/**
			 * @method Base#getUrlParam
			 */
			getUrlParam: {
				private: false,
				public: true,
				value(context, name) {
					let returnString = this.data.queryParams[name];
					return this.isString(returnString) ? returnString : null;
				},
			},

			/**
			 * @method Base#onPageChange
			 */
			onPageChange: {
				public: true,
				value(context, fn, options = {}) {
					const { data } = this;
					const { pageChangeHandlers } = data;
					const { length } = pageChangeHandlers;
					const owner = context;
					let handlerFn = fn;
					let isNewListener;

					if (options.once === true) {
						// wrap the passed function to auto-remove after first call
						handlerFn = (...args) => {
							fn.apply(owner, args);
							this.offPageChange({ owner, fn: handlerFn });
						};
					}

					pageChangeHandlers.push({ fn: handlerFn, owner });
					data.pageChangeHandlers = this.unique(pageChangeHandlers);
					isNewListener = data.pageChangeHandlers.length > length;

					return isNewListener;
				},
			},

			/**
			 * Used to capture insights about the user behavior, converting from Tixr's schema to the more generic Insights one.
			 * @method Base#captureInsight
			 */
			captureInsight: {
				public: true,
				value(context, options) {
					// NOTE: this function exists to be overridden by site specific App.js

					// Pass through the options, so it will be the first argument in the overridden function
					return options;
				},
			},

			onDie: {
				private: false,
				public: true,
				value(context) {
					// trigger the base level 'on die' actions

					this.hideTooltip({
						// remove all tooltips requested
						owner: context,
					});

					this.offScroll({
						// remove any scrolling listeners requested
						owner: context,
					});

					this.offPageChange({
						// remove any navigation listeners requested
						owner: context,
					});

					this.offTick({
						// remove any tick listeners requested
						owner: context,
					});
				},
			},
		},

		destructor() {
			this.offTick({
				owner: null,
			});
		},
	});
});

/**
 * The core application instance driving a generated website. App inherits functionality from Base, and is responsible for things like registering page URL handlers.
 * @namespace App
 * @augments Base
 * @example
 * // site project has App.js file, for example:
 * return Base.inherit({
 *   namespace: "App",
 *   prepare(setup) {
 *     // Landing Page
 *     this.handlePath({
 *       paths: [`/`, `/landing`],
 *       handler: (request, response) => {
 *         response.page = `Landing`;
 *       },
 *     });
 *   },
 *   singleton: true,
 * });
 */

//# sourceMappingURL=Base.js.map